/**
 * Currency formatting utilities for consistent display of monetary values
 */

/**
 * Formats a number as currency with thousands separators and 2 decimal places
 * @param {number|string} value - The value to format
 * @returns {string} Formatted currency string (e.g., "1,234.56")
 */
export const formatCurrency = (value) => {
  const num = parseFloat(value) || 0;
  return num.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });
};

/**
 * Formats a number as currency with dollar sign and thousands separators
 * @param {number|string} value - The value to format
 * @returns {string} Formatted currency string with dollar sign (e.g., "$1,234.56")
 */
export const formatCurrencyWithSymbol = (value) => {
  return `$${formatCurrency(value)}`;
};

/**
 * Formats a number as abbreviated currency for dashboard/summary displays
 * @param {number|string} value - The value to format
 * @param {boolean} showCents - Whether to show cents for values under 1000
 * @returns {string} Formatted abbreviated currency (e.g., "$1.2k", "$156.8k")
 */
export const formatCurrencyAbbreviated = (value, showCents = false) => {
  const num = parseFloat(value) || 0;
  
  if (num >= 1000000) {
    return `$${(num / 1000000).toLocaleString('en-US', { 
      minimumFractionDigits: 1, 
      maximumFractionDigits: 1 
    })}M`;
  } else if (num >= 1000) {
    return `$${(num / 1000).toLocaleString('en-US', { 
      minimumFractionDigits: 1, 
      maximumFractionDigits: 1 
    })}k`;
  } else {
    if (showCents) {
      return formatCurrencyWithSymbol(num);
    } else {
      return `$${Math.round(num).toLocaleString('en-US')}`;
    }
  }
};

/**
 * Safely converts a value to a number for currency calculations
 * @param {any} value - The value to convert
 * @returns {number} Numeric value or 0 if invalid
 */
export const safeNumber = (value) => {
  const num = parseFloat(value);
  return isNaN(num) ? 0 : num;
};
